import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { searchApi } from '../../shared/services/crm.service';
import { crmKeys } from '../../shared/constants/queryKeys';

const DEBOUNCE_MS = 250;

/**
 * Global CRM search. Debounces the input value before reaching the network
 * so a typing burst (one rerender per keystroke) fans out into a single
 * request after the user pauses. The initial debounced value is empty so
 * the very first render with a valid query still waits the full debounce
 * window — without this, mounting the search dialog with a 2-char query
 * would fire immediately. See R30-004 audit, gap E.
 */
export function useGlobalSearch(query: string, types?: string[]) {
  const trimmed = query.trim();
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(trimmed), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [trimmed]);

  return useQuery({
    queryKey: crmKeys.search(debouncedQuery, types),
    queryFn: () => searchApi.search({ q: debouncedQuery, types, limit: 5 }),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}
